import { useMemo } from 'react';

export type Symbol = {
  name: string;
  kind: 'function' | 'class' | 'interface' | 'type' | 'const' | 'let' | 'var' | 'enum' | 'method' | 'property';
  line: number;
  column: number;
  icon: string;
};

// Regex patterns for different symbol types
const patterns = {
  // function name() or async function name()
  function: /(?:export\s+)?(?:async\s+)?function\s+(\w+)/g,
  // const name = () => or const name = function
  arrowFunction: /(?:export\s+)?(?:const|let|var)\s+(\w+)\s*=\s*(?:async\s*)?\(/g,
  // class Name
  class: /(?:export\s+)?(?:abstract\s+)?class\s+(\w+)/g,
  // interface Name
  interface: /(?:export\s+)?interface\s+(\w+)/g,
  // type Name =
  type: /(?:export\s+)?type\s+(\w+)\s*=/g,
  // const/let/var NAME = (not arrow functions)
  // eslint-disable-next-line no-useless-escape
  variable: /(?:export\s+)?(?:const|let|var)\s+(\w+)\s*=\s*(?![(\[]|async\s*\(|function)/g,
  // enum Name
  enum: /(?:export\s+)?enum\s+(\w+)/g,
  // React.memo(function Name or React.memo(Name
  reactMemo: /React\.memo\s*\(\s*(?:function\s+)?(\w+)/g,
  // export default function Name
  exportDefault: /export\s+default\s+(?:async\s+)?function\s+(\w+)/g,
};

const kindIcons: Record<Symbol['kind'], string> = {
  function: 'ƒ',
  class: 'C',
  interface: 'I',
  type: 'T',
  const: '◇',
  let: '◇',
  var: '◇',
  enum: 'E',
  method: 'm',
  property: '○',
};

export function useSymbols(code: string) {
  const symbols = useMemo<Symbol[]>(() => {
    if (!code) return [];

    const result: Symbol[] = [];
    const lines = code.split('\n');
    const seen = new Set<string>();

    // Helper to add symbol at correct line
    const addSymbol = (name: string, kind: Symbol['kind'], lineContent: string, lineIndex: number) => {
      const key = `${name}-${lineIndex}`;
      if (seen.has(key)) return;
      seen.add(key);

      const column = lineContent.indexOf(name) + 1;
      result.push({
        name,
        kind,
        line: lineIndex + 1,
        column: column > 0 ? column : 1,
        icon: kindIcons[kind],
      });
    };

    // Process line by line for better line number accuracy
    lines.forEach((line, lineIndex) => {
      // Skip comments
      if (line.trim().startsWith('//') || line.trim().startsWith('*')) return;

      // Check each pattern
      let match;

      // Functions
      patterns.function.lastIndex = 0;
      while ((match = patterns.function.exec(line)) !== null) {
        addSymbol(match[1], 'function', line, lineIndex);
      }

      // Arrow functions
      patterns.arrowFunction.lastIndex = 0;
      while ((match = patterns.arrowFunction.exec(line)) !== null) {
        addSymbol(match[1], 'function', line, lineIndex);
      }

      // Classes
      patterns.class.lastIndex = 0;
      while ((match = patterns.class.exec(line)) !== null) {
        addSymbol(match[1], 'class', line, lineIndex);
      }

      // Interfaces
      patterns.interface.lastIndex = 0;
      while ((match = patterns.interface.exec(line)) !== null) {
        addSymbol(match[1], 'interface', line, lineIndex);
      }

      // Types
      patterns.type.lastIndex = 0;
      while ((match = patterns.type.exec(line)) !== null) {
        addSymbol(match[1], 'type', line, lineIndex);
      }

      // Enums
      patterns.enum.lastIndex = 0;
      while ((match = patterns.enum.exec(line)) !== null) {
        addSymbol(match[1], 'enum', line, lineIndex);
      }

      // React.memo components
      patterns.reactMemo.lastIndex = 0;
      while ((match = patterns.reactMemo.exec(line)) !== null) {
        addSymbol(match[1], 'function', line, lineIndex);
      }

      // Export default functions
      patterns.exportDefault.lastIndex = 0;
      while ((match = patterns.exportDefault.exec(line)) !== null) {
        addSymbol(match[1], 'function', line, lineIndex);
      }

      // Variables (const/let/var)
      patterns.variable.lastIndex = 0;
      while ((match = patterns.variable.exec(line)) !== null) {
        // Skip if it's actually a destructuring
        if (line.includes('{') && line.indexOf('{') < line.indexOf(match[1])) continue;
        addSymbol(match[1], 'const', line, lineIndex);
      }
    });

    // Sort by line number
    return result.sort((a, b) => a.line - b.line);
  }, [code]);

  return { symbols };
}

export function filterSymbols(symbols: Symbol[], query: string): Symbol[] {
  if (!query) return symbols;

  const lowerQuery = query.toLowerCase();

  // Check for kind filter (e.g., "@function" or ":function")
  if (query.startsWith('@') || query.startsWith(':')) {
    const kindFilter = query.slice(1).toLowerCase();
    return symbols.filter((s) => s.kind.toLowerCase().startsWith(kindFilter));
  }

  return symbols.filter((s) => s.name.toLowerCase().includes(lowerQuery));
}
